import React, { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import {
  Box,
  Toolbar,
  Collapse,
  Stack,
  useMediaQuery,
  useTheme,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  InputAdornment,
  InputBase
} from '@mui/material';
/* eslint-disable no-restricted-imports -- ui Select has no `multiple` (activityTypes filter only) */
import MuiSelect from '@mui/material/Select';
import { CustomSlider } from '@sm-dashboard/form-provider';
import { DatePicker, Input, IconButton, Select, Typography } from '@sm-dashboard/ui-components';
import ClearIcon from '@mui/icons-material/Clear';
import SearchOutlined from '@mui/icons-material/Search';
import { FilterConfig, FilterOptionType, FilterType } from '@sm-dashboard/types';
import { ActionButtons, isSearchFilter, MobilePrimaryFilterRow } from '@sm-dashboard/grid-data';

const ACTIVITY_DIALOG_FILTER_WIDTH = 200;
const activityTypesOptions: FilterOptionType[] = [
  { name: 'תרומה', value: 'donation' },
  { name: 'רכישה', value: 'purchase' },
  { name: 'פנייה', value: 'lead' },
  { name: 'איש קשר', value: 'contact' }
];

const getFiltersConfig = (): FilterConfig<any>[] => [
  {
    field: 'searchTerm',
    label: 'חיפוש',
    type: 'text' as FilterType,
    show: true,
    level: 'primary'
  },
  {
    field: 'activityTypes',
    label: 'סוג פעילות',
    type: 'multiSelect',
    options: activityTypesOptions,
    show: true,
    width: ACTIVITY_DIALOG_FILTER_WIDTH
  },
  {
    field: 'startDate',
    label: 'מתאריך',
    type: 'date' as FilterType,
    show: true,
    width: ACTIVITY_DIALOG_FILTER_WIDTH
  },
  {
    field: 'endDate',
    label: 'עד תאריך',
    type: 'date' as const,
    show: true,
    width: ACTIVITY_DIALOG_FILTER_WIDTH
  }
];

const convertUniqueValues = (arr: (string | number)[]) => {
  const stringArr = arr.map(item => item.toString());
  const doubleValues = stringArr.filter((item: string, index: number) => stringArr.indexOf(item) !== index);
  return stringArr.filter((item: string, index: number) => doubleValues.indexOf(item) === -1);
};

const useDebounce = (callback: (...args: any[]) => void, delay: number) => {
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  const debouncedCallback = useCallback(
    (...args: any[]) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      const newTimeoutId = setTimeout(() => {
        callback(...args);
      }, delay);
      setTimeoutId(newTimeoutId);
    },
    [callback, delay, timeoutId]
  );

  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  return debouncedCallback;
};

interface ActivitiesFiltersProps {
  onFiltersChange: (filters: Record<string, string | string[] | number[] | null>) => void;
  totalResults?: number;
}

export const ActivitiesFilters: React.FC<ActivitiesFiltersProps> = ({ onFiltersChange, totalResults }) => {
  const [localFilters, setLocalFilters] = useState<Record<string, string | string[] | number[] | null>>({});
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const filters = useMemo(() => getFiltersConfig(), []);

  const hasActiveFilters = useMemo(() => {
    return Object.keys(localFilters).length > 0;
  }, [localFilters]);

  const visibleFilters = useMemo(() => {
    return filters.filter(filter => filter.show === undefined || filter.show);
  }, [filters]);

  const [primaryFilters, regularFilters] = useMemo(() => {
    if (!visibleFilters) return [[], []];
    const primary: typeof visibleFilters = [];
    const regular: typeof visibleFilters = [];
    visibleFilters.forEach(filter => {
      if (filter.level === 'primary') {
        primary.push(filter);
      } else if (filter.level === 'regular' || !filter.level) {
        regular.push(filter);
      }
    });
    if (primary.length === 0 && regular.length > 0) {
      primary.push(regular[0]);
      regular.splice(0, 1);
    }
    return [primary, regular];
  }, [visibleFilters]);

  const handleApplyFilters = useCallback(
    (filter: FilterConfig<any>, value: any) => {
      const newFilters = { ...localFilters };

      if (filter.type === 'multiSelect' || filter.type === 'sliderRange') {
        if (value?.length > 0) {
          newFilters[String(filter.field)] = value;
        } else {
          delete newFilters[String(filter.field)];
        }
      } else if (filter.type === 'autocomplete' && value?.value) {
        if (typeof value?.value === 'string' && value?.value.includes('-')) {
          newFilters[String(filter.field)] = String(value?.value.split('-')[1]);
        } else {
          newFilters[String(filter.field)] = value?.value;
        }
      } else {
        if (value !== undefined && value !== null && value !== '') {
          newFilters[String(filter.field)] = value;
        } else {
          delete newFilters[String(filter.field)];
        }
      }

      setLocalFilters(newFilters);
      onFiltersChange(newFilters);
    },
    [localFilters, onFiltersChange]
  );

  const handleResetAllFilters = useCallback(() => {
    setLocalFilters({});
    onFiltersChange({});
  }, [onFiltersChange]);

  const FilterInput: React.FC<{
    filter: FilterConfig<any>;
    isPrimary?: boolean;
    currentValue: any;
    onFilterChange: (value: any) => void;
  }> = ({ filter, isPrimary, currentValue, onFilterChange }) => {
    const handleFilterChange = useCallback(
      (value: any) => {
        if (filter.type === 'multiSelect') {
          if (!Array.isArray(value) || value.length === 0) {
            onFilterChange([]);
            return;
          }
          value = convertUniqueValues(value);
        }
        onFilterChange(value);
      },
      [filter, onFilterChange]
    );

    const debouncedHandleFilterChange = useDebounce(handleFilterChange, 1000);
    const [textValue, setTextValue] = useState<string>(() => {
      return typeof currentValue === 'string' ? currentValue : filter.defaultValue || '';
    });

    useEffect(() => {
      if (filter.type === 'text' || filter.type === 'number' || filter.type === undefined) {
        setTextValue(typeof currentValue === 'string' ? currentValue : filter.defaultValue || '');
      }
    }, [currentValue, filter.field, filter.type, filter.defaultValue]);

    const forHandleFilterChange = (value: any) => {
      if (filter.type === 'text' || filter.type === 'number' || filter.type === undefined) {
        setTextValue(value);
        debouncedHandleFilterChange(value);
      } else {
        handleFilterChange(value);
      }
    };

    const flattenSelectOptions = (options: FilterOptionType[], prefix = ''): { value: string; label: string }[] => {
      const result: { value: string; label: string }[] = [];
      for (const option of options) {
        const name = String(option.label || option.name || option.value);
        if (option.options?.length) {
          result.push(...flattenSelectOptions(option.options, `${name} — `));
        } else {
          result.push({ value: String(option.value), label: prefix + name });
        }
      }
      return result;
    };

    switch (filter.type) {
      case 'date':
        return (
          <Box
            sx={{
              minWidth: 100,
              width: !isSmallScreen ? filter.width ?? ACTIVITY_DIALOG_FILTER_WIDTH : '100%',
              flexShrink: 0,
              '& .MuiInputBase-root.SmInputBase-root': {
                minHeight: 44,
                boxSizing: 'border-box'
              }
            }}
          >
            <DatePicker
              appearance="plain"
              value={(typeof currentValue === 'string' ? currentValue : filter.defaultValue) || ''}
              onChange={forHandleFilterChange}
              label={filter.label}
            />
          </Box>
        );
      case 'select':
        return (
          <Box sx={{ minWidth: 100, width: !isSmallScreen ? filter.width : '100%' }}>
            <Select
              appearance="plain"
              label={filter.label}
              value={currentValue != null && currentValue !== '' ? String(currentValue) : ''}
              onChange={v => forHandleFilterChange(v || null)}
              options={flattenSelectOptions(filter.options || [])}
            />
          </Box>
        );
      case 'multiSelect': {
        const options = filter.options || [];
        const selected: string[] = Array.isArray(currentValue)
          ? currentValue.map(String)
          : !currentValue
            ? []
            : typeof currentValue === 'string'
              ? currentValue.split(',')
              : [];
        const labelByValue = new Map(options.map(o => [String(o.value), String(o.name || o.label || o.value)]));
        const clearAdornment =
          selected.length > 0 ? (
            <InputAdornment position="end" className="SmInputAdornment-root">
              <Box
                component="span"
                onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
                sx={{ display: 'inline-flex' }}
              >
                <IconButton
                  size="small"
                  shape="circle"
                  color="secondary"
                  aria-label="נקה"
                  onClick={e => {
                    e.stopPropagation();
                    forHandleFilterChange([]);
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Box>
            </InputAdornment>
          ) : undefined;
        return (
          <Box
            sx={{
              minWidth: 100,
              width: !isSmallScreen ? filter.width ?? ACTIVITY_DIALOG_FILTER_WIDTH : '100%',
              flexShrink: 0
            }}
          >
            <FormControl
              variant="standard"
              fullWidth
              sx={{
                minWidth: 100,
                '& .MuiInputBase-root.SmInputBase-root': {
                  alignItems: 'center',
                  minHeight: 44,
                  boxSizing: 'border-box'
                },
                '& .MuiSelect-select': {
                  padding: '10px 0',
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: 'unset'
                }
              }}
            >
              <InputLabel className="SmInputLabel-root" shrink>
                {filter.label}
              </InputLabel>
              <MuiSelect
                multiple
                className="SmSelect-root SmSelect-appearance-plain"
                value={selected}
                onChange={e => {
                  const next = e.target.value;
                  forHandleFilterChange(Array.isArray(next) && next.length ? next : []);
                }}
                input={
                  <InputBase
                    className="SmInputBase-root SmInputBase-appearance-plain"
                    endAdornment={clearAdornment}
                    sx={{
                      '&:before': { display: 'none' },
                      '&:after': { display: 'none' }
                    }}
                  />
                }
                MenuProps={{
                  PaperProps: { className: 'SmSelectMenu-paper' },
                  MenuListProps: { className: 'SmSelectMenu-list' }
                }}
                renderValue={values => (
                  <span className="SmSelect-value">
                    {(values as string[])
                      .map(v => labelByValue.get(v) ?? v)
                      .join(', ')}
                  </span>
                )}
              >
                {options.map(option => (
                  <MenuItem key={String(option.value)} value={String(option.value)}>
                    {option.name || option.label}
                  </MenuItem>
                ))}
              </MuiSelect>
            </FormControl>
          </Box>
        );
      }
      case 'text':
      case 'number':
      case undefined:
        return (
          <Box sx={{ width: !isSmallScreen ? filter.width : '100%' }}>
            <Input
              appearance={isSearchFilter(filter) ? 'default' : 'plain'}
              fullWidth={!filter.width || isSmallScreen}
              placeholder={filter.label}
              value={textValue}
              onChange={e => forHandleFilterChange(e.target.value)}
              type={filter.type === 'number' ? 'number' : 'text'}
              startIcon={isSearchFilter(filter) ? <SearchOutlined /> : undefined}
              endIcon={
                textValue && textValue !== filter.defaultValue ? (
                  <IconButton
                    size="small"
                    shape="circle"
                    color="secondary"
                    aria-label="נקה"
                    onClick={() => {
                      setTextValue(filter.defaultValue || '');
                      forHandleFilterChange(null);
                    }}
                  >
                    <ClearIcon />
                  </IconButton>
                ) : undefined
              }
            />
          </Box>
        );
      case 'sliderRange': {
        if (isSmallScreen) {
          const sliderValue = Array.isArray(currentValue)
            ? currentValue
            : filter.defaultValue || [filter.min || 0, filter.max || 100000];
          const [minValue, maxValue] = sliderValue;
          return (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '100%' }}>
              <Box sx={{ flex: 1 }}>
                <Input
                  appearance="plain"
                  type="number"
                  placeholder="מסכום"
                  value={minValue}
                  onChange={e => {
                    const newValue = [e.target.value, maxValue];
                    forHandleFilterChange(newValue);
                  }}
                  inputProps={{ min: filter.min, max: filter.max }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Input
                  appearance="plain"
                  type="number"
                  placeholder="עד סכום"
                  value={maxValue}
                  onChange={e => {
                    const newValue = [minValue, e.target.value];
                    forHandleFilterChange(newValue);
                  }}
                  inputProps={{ min: filter.min, max: filter.max }}
                />
              </Box>
            </Box>
          );
        }
        const sliderValue = Array.isArray(currentValue)
          ? currentValue
          : filter.defaultValue || [filter.min || 0, filter.max || 100000];
        return (
          <Box sx={{ width: !isSmallScreen ? filter.width || 200 : '100%', px: 1 }}>
            <CustomSlider
              value={sliderValue}
              onChange={(value: number | number[]) => {
                if (Array.isArray(value)) {
                  forHandleFilterChange(value);
                }
              }}
              min={filter.min || 0}
              max={filter.max || 100000}
              label={filter.label}
            />
          </Box>
        );
      }
      default:
        return null;
    }
  };

  const groupFilters = (filtersToGroup: FilterConfig<any>[]) => {
    const grouped: { [key: number]: FilterConfig<any>[] } = {};
    filtersToGroup.forEach(filter => {
      const group = filter.group || 0;
      if (!grouped[group]) {
        grouped[group] = [];
      }
      grouped[group].push(filter);
    });
    return grouped;
  };

  const renderMobileGroup = (filtersInGroup: FilterConfig<any>[], isPrimary?: boolean) => {
    const elements: JSX.Element[] = [];
    for (let i = 0; i < filtersInGroup.length; i++) {
      const current = filtersInGroup[i];
      const next = filtersInGroup[i + 1];
      const currentValue =
        localFilters[String(current.field)] !== undefined
          ? localFilters[String(current.field)]
          : current.defaultValue || '';
      const handleCurrentChange = (value: any) => handleApplyFilters(current, value);

      if (current?.type === 'date' && next?.type === 'date') {
        const nextValue =
          localFilters[String(next.field)] !== undefined ? localFilters[String(next.field)] : next.defaultValue || '';
        const handleNextChange = (value: any) => handleApplyFilters(next, value);

        elements.push(
          <Box key={`pair-${i}`} sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <FilterInput
                filter={current}
                isPrimary={isPrimary}
                currentValue={currentValue}
                onFilterChange={handleCurrentChange}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <FilterInput
                filter={next}
                isPrimary={isPrimary}
                currentValue={nextValue}
                onFilterChange={handleNextChange}
              />
            </Box>
          </Box>
        );
        i++;
      } else {
        elements.push(
          <Fragment key={i}>
            <FilterInput
              filter={current}
              isPrimary={isPrimary}
              currentValue={currentValue}
              onFilterChange={handleCurrentChange}
            />
          </Fragment>
        );
      }
    }
    return elements;
  };

  const renderFiltersContent = useCallback(
    (filtersToRender: FilterConfig<any>[], isPrimary?: boolean) => {
      const groupedFilters = groupFilters(filtersToRender);

      if (isPrimary) {
        return (
          <Fragment>
            {Object.entries(groupedFilters).map(([groupKey, groupFilters]) =>
              isSmallScreen ? (
                <Fragment key={groupKey}>{renderMobileGroup(groupFilters, isPrimary)}</Fragment>
              ) : (
                <Fragment key={groupKey}>
                  {groupFilters.map((filter: FilterConfig<any>, index) => {
                    const currentValue =
                      localFilters[String(filter.field)] !== undefined
                        ? localFilters[String(filter.field)]
                        : filter.defaultValue || '';
                    const handleFilterChange = (value: any) => handleApplyFilters(filter, value);
                    return (
                      <Fragment key={index}>
                        <FilterInput
                          filter={filter}
                          isPrimary={isPrimary}
                          currentValue={currentValue}
                          onFilterChange={handleFilterChange}
                        />
                      </Fragment>
                    );
                  })}
                </Fragment>
              )
            )}
          </Fragment>
        );
      }

      return (
        <Stack gap={1.5} width="100%" sx={{ mt: 1 }}>
          {Object.entries(groupedFilters).map(([groupKey, groupFilters]) => {
            return (
              <Box
                key={groupKey}
                sx={{
                  display: 'flex',
                  flexDirection: isSmallScreen ? 'column' : 'row',
                  flexWrap: 'wrap',
                  alignItems: 'flex-end',
                  gap: 1.5,
                  width: '100%'
                }}
              >
                {isSmallScreen
                  ? renderMobileGroup(groupFilters)
                  : groupFilters.map((filter: FilterConfig<any>, index) => {
                      const currentValue =
                        localFilters[String(filter.field)] !== undefined
                          ? localFilters[String(filter.field)]
                          : filter.defaultValue || '';
                      const handleFilterChange = (value: any) => handleApplyFilters(filter, value);
                      return (
                        <Fragment key={index}>
                          <FilterInput
                            filter={filter}
                            currentValue={currentValue}
                            onFilterChange={handleFilterChange}
                          />
                        </Fragment>
                      );
                    })}
              </Box>
            );
          })}
        </Stack>
      );
    },
    [localFilters, handleApplyFilters, isSmallScreen, renderMobileGroup]
  );

  const actionButtonsProps = {
    hasActiveFilters,
    onReset: handleResetAllFilters,
    regularFiltersLength: regularFilters.length,
    expanded,
    onToggle: () => setExpanded(!expanded),
    exportConfig: undefined,
    onExcelClick: () => {
      // Export functionality not available in activities dialog
    },
    openExcel: false,
    exportDisabled: true,
    loading: false,
    anchorEl: null,
    exportMenuItems: undefined,
    onMenuItemClick: () => () => {
      // Export menu item click handler not available
    },
    onCloseMenu: () => {
      // Close menu handler not available
    }
  };

  const searchFilter = primaryFilters.find(isSearchFilter);
  const otherPrimaryFilters = primaryFilters.filter(filter => !isSearchFilter(filter));
  const useMobileSearchRow = isSmallScreen && Boolean(searchFilter);

  return (
    <Stack gap={1} sx={{ p: 2 }}>
      <Grid
        container
        sx={{
          gap: 1,
          width: '100%',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        {!isSmallScreen && (
          <Grid item md={6}>
            {totalResults !== undefined && (
              <Box sx={{ color: 'text.secondary', textAlign: 'left' }}>
                <Typography variant="body">נמצאו {totalResults.toLocaleString('he-IL')} תוצאות</Typography>
              </Box>
            )}
          </Grid>
        )}
        {useMobileSearchRow && searchFilter ? (
          <Grid item xs={12}>
            <MobilePrimaryFilterRow
              hasSearchFilter
              getSearchValue={() => {
                const value = localFilters[String(searchFilter.field)];
                return typeof value === 'string' ? value : '';
              }}
              onClearSearch={() => handleApplyFilters(searchFilter, null)}
              searchInput={
                <FilterInput
                  filter={searchFilter}
                  isPrimary
                  currentValue={
                    localFilters[String(searchFilter.field)] !== undefined
                      ? localFilters[String(searchFilter.field)]
                      : searchFilter.defaultValue || ''
                  }
                  onFilterChange={value => handleApplyFilters(searchFilter, value)}
                />
              }
              otherPrimaryContent={
                otherPrimaryFilters.length > 0 ? renderFiltersContent(otherPrimaryFilters, true) : undefined
              }
              hasActiveFilters={actionButtonsProps.hasActiveFilters}
              onReset={actionButtonsProps.onReset}
              regularFiltersLength={actionButtonsProps.regularFiltersLength}
              expanded={actionButtonsProps.expanded}
              onToggleFilters={actionButtonsProps.onToggle}
              exportProps={{
                exportConfig: actionButtonsProps.exportConfig,
                onExcelClick: actionButtonsProps.onExcelClick,
                openExcel: actionButtonsProps.openExcel,
                exportDisabled: actionButtonsProps.exportDisabled,
                loading: actionButtonsProps.loading,
                anchorEl: actionButtonsProps.anchorEl,
                exportMenuItems: actionButtonsProps.exportMenuItems,
                onMenuItemClick: actionButtonsProps.onMenuItemClick,
                onCloseMenu: actionButtonsProps.onCloseMenu
              }}
            />
          </Grid>
        ) : (
          <>
            {primaryFilters.length > 0 && (
              <Grid item xs={true} md>
                {renderFiltersContent(primaryFilters, true)}
              </Grid>
            )}

            <Grid item xs={'auto'}>
              <ActionButtons {...actionButtonsProps} />
            </Grid>
          </>
        )}
      </Grid>
      {isSmallScreen && totalResults !== undefined && (
        <Box sx={{ textAlign: 'left', mt: 1 }}>
          <Box sx={{ color: 'text.secondary' }}>
            <Typography variant="body">נמצאו {totalResults.toLocaleString('he-IL')} תוצאות</Typography>
          </Box>
        </Box>
      )}

      <Collapse in={expanded}>
        <Toolbar
          disableGutters
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1,
            mb: 2,
            px: 2
          }}
        >
          {renderFiltersContent(regularFilters, false)}
        </Toolbar>
      </Collapse>
    </Stack>
  );
};
